import React from "react";
import classNames from "classnames";
import * as R from "ramda";

import { Section } from "components/Section";
import { useGetText } from "components/Text";
import Fields from "components/guides/Fields";
import {
  Icon as PermissionIcon,
  Message as PermissionMessage,
} from "components/guides/ZoningDetails/Permission";
import ZoningDetails from "components/guides/ZoningDetails/ZoningDetails";
import EventDetails from "components/guides/summary/EventDetails";
import ZoningMap from "components/guides/summary/ZoningMap";
import EventMap from "components/maps/EventMap";
import GeomsMap from "components/maps/GeomsMap";
import SummaryTable from "components/projects/SummaryTable";
import { useAnswerContext } from "contexts/answerContext";
import { useRootFieldsList } from "queries/entities";
import { getGuideFieldIdents, isFieldAnswered } from "queries/projects";
import { isCalculated } from "reducers/fields";
import { getIsZoningEnabled, getZoning } from "reducers/projects";
import { isBlank } from "utils/func";
import { getPermission } from "utils/zoning";

import styles from "./Summary.scss";

const SPECIAL_EVENTS_LOCATION_FIELD_KEYS = [
  "event_geoms",
  "event_addresses",
  "event_location",
  "event_route",
];

const LOCATION_FIELD_KEYS = [...SPECIAL_EVENTS_LOCATION_FIELD_KEYS, "address"];

const useAnsweredFields = ({ select = R.identity }) => {
  const { record: project } = useAnswerContext();
  return useRootFieldsList({
    select: R.filter((f) => {
      if (!isFieldAnswered(project, f.key)) {
        return false;
      }
      return select(f);
    }),
  });
};

const isLocationField = R.propSatisfies((key) => R.includes(key, LOCATION_FIELD_KEYS), "key");
const useAnsweredLocationFields = () => useAnsweredFields({ select: isLocationField });

const useIsSpecialEventsLocation = () => {
  const isSpecialEvent = R.propSatisfies(
    (key) => R.includes(key, SPECIAL_EVENTS_LOCATION_FIELD_KEYS),
    "key",
  );
  const { data: fields = [] } = useAnsweredFields({ select: isSpecialEvent });
  return fields.length > 0;
};

const useAddressType = () => {
  const { data: answeredLocationFields = [] } = useAnsweredLocationFields();
  const locationKeys = R.map(R.prop("key"), answeredLocationFields);

  if (locationKeys.includes("event_geoms")) return "geoms";
  if (locationKeys.includes("event_addresses")) return "geoms";
  if (locationKeys.includes("event_location")) return "location";
  if (locationKeys.includes("event_route")) return "location";
  if (locationKeys.includes("address")) return "address";
  return null;
};

const isGuideField = R.curry((project, field) =>
  R.includes(field.identifier, getGuideFieldIdents(project)),
);

const useGuideFields = () => {
  const { record: project } = useAnswerContext();
  return useAnsweredFields({
    select: R.allPass([
      R.complement(isLocationField),
      R.complement(isCalculated),
      isGuideField(project),
    ]),
  });
};

export const useIsViewable = () => {
  const { data: guideFields = [] } = useGuideFields();
  const { data: answeredLocationFields = [] } = useAnsweredLocationFields();
  if (answeredLocationFields.length || guideFields.length) return true;

  return false;
};

const Map = () => {
  const addressType = useAddressType();
  const { data: answeredLocationFields = [] } = useAnsweredLocationFields();
  if (!addressType) return null;

  switch (addressType) {
    case "geoms": {
      return <GeomsMap fields={answeredLocationFields} />;
    }
    case "location": {
      return <EventMap />;
    }
    default: {
      return <ZoningMap />;
    }
  }
};

const LocationDetails = () => {
  const getText = useGetText();
  const { data: answeredLocationFields = [] } = useAnsweredLocationFields();

  const isSpecialEvent = useIsSpecialEventsLocation();
  if (answeredLocationFields.length === 0) return null;

  return (
    <div className={styles.fields}>
      {isSpecialEvent ? (
        <EventDetails />
      ) : (
        <>
          <Section heading={getText("guides.summary.location")}>
            <Fields
              fields={answeredLocationFields}
              behaviorType="disabled"
              hideRequired
              hideHelp
              hideLabel
            />
          </Section>
          <ZoningDetails fromSummary />
        </>
      )}
    </div>
  );
};

const Permission = () => {
  const { record: project } = useAnswerContext();
  const zoning = getZoning(project);
  const isZoningEnabled = getIsZoningEnabled(project);

  if (!isZoningEnabled || isBlank(zoning)) return null;

  const permission = getPermission(zoning);
  if (!permission) return null;

  return (
    <div className={styles.permission}>
      <PermissionIcon permission={permission} size="1x" />
      <PermissionMessage permission={permission} />
    </div>
  );
};

const Summary = () => {
  const { record } = useAnswerContext();
  const hideSidebar = R.pathEq("zoning", ["guide", "guide_type"], record);

  return (
    <div className={styles.container} data-handoff-summary>
      <Permission />
      <Map />
      <div className={classNames({ [styles.sideBySide]: !hideSidebar })}>
        <LocationDetails />
        {hideSidebar || (
          <div className={styles.sidebar}>
            <SummaryTable isAriaHidden />
          </div>
        )}
      </div>
      <ProjectDetails />
    </div>
  );
};

const ProjectDetails = () => {
  const getText = useGetText();
  const isSpecialEvent = useIsSpecialEventsLocation();
  const { data: guideFields = [] } = useGuideFields();
  if (guideFields.length === 0) return null;

  return (
    <div className={styles.projectDetails}>
      <Section
        heading={getText(
          isSpecialEvent ? "guides.summary.event_details" : "guides.summary.project_details",
        )}
      >
        <Fields
          fields={guideFields}
          behaviorType="disabled"
          className={styles.fieldColumns}
          hideRequired
          hideHelp
        />
      </Section>
    </div>
  );
};

export default Summary;
